import { useEffect, useState } from 'react';

import {
  ChakraFlex,
  FireflyButton,
  FormLabel,
  ModalV2,
  TextAreaInputV2,
  UniversalSelect,
} from '@equip.health/ui';

import { ALERT_DISMISS_REASON_LABEL } from '~/lib/constants/providerDashboard.constants';
import { AlertDismissReason } from '~/lib/types/providerDashboard';

const DISMISS_REASON_OPTIONS = Object.entries(ALERT_DISMISS_REASON_LABEL).map(
  ([value, label]) => ({ value, label }),
);

const TOP_LEVEL_ID = 'dismiss-alert-modal';
const REASON_ID = `${TOP_LEVEL_ID}__reason`;

interface DismissAlertModalProps {
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: (reason: AlertDismissReason, details?: string) => void;
}

const DismissAlertModal = ({
  isOpen,
  isLoading,
  onClose,
  onConfirm,
}: DismissAlertModalProps) => {
  const [selectedReason, setSelectedReason] =
    useState<AlertDismissReason | null>(null);
  const [details, setDetails] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setSelectedReason(null);
      setDetails('');
    }
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  const handleConfirm = () => {
    if (!selectedReason) return;
    onConfirm(selectedReason, details.trim() || undefined);
  };

  const selectedOption = selectedReason
    ? {
        value: selectedReason,
        label: ALERT_DISMISS_REASON_LABEL[selectedReason],
      }
    : null;

  return (
    <ModalV2
      id={TOP_LEVEL_ID}
      isOpen={isOpen}
      onClose={handleClose}
      title="Dismiss this alert?"
      size="lg"
      footerContent={
        <ChakraFlex justifyContent="space-between" width="100%">
          <FireflyButton
            id={`${TOP_LEVEL_ID}__cancel`}
            onClick={handleClose}
            variant="secondary"
          >
            Cancel
          </FireflyButton>
          <FireflyButton
            id={`${TOP_LEVEL_ID}__confirm`}
            isDisabled={!selectedReason}
            isLoading={isLoading}
            onClick={handleConfirm}
          >
            Dismiss alert
          </FireflyButton>
        </ChakraFlex>
      }
    >
      <ChakraFlex direction="column" gap="20px">
        <ChakraFlex direction="column" gap="8px">
          <FormLabel id={`${REASON_ID}-label`} label="Reason" shouldHideOptionalLabel />
          <UniversalSelect
            id={REASON_ID}
            isSingleSelect
            modalId={TOP_LEVEL_ID}
            placeholder="Select a reason"
            options={DISMISS_REASON_OPTIONS}
            value={selectedOption}
            onChange={(option: { value: string } | null) =>
              setSelectedReason(
                (option?.value as AlertDismissReason) ?? null,
              )
            }
          />
        </ChakraFlex>

        <ChakraFlex direction="column" gap="8px">
          <FormLabel id={`${TOP_LEVEL_ID}__details-label`} label="Details" isRequired={false} />
          <TextAreaInputV2
            id={`${TOP_LEVEL_ID}__details`}
            onChange={setDetails}
            placeholder="Add any context for the care team"
            value={details}
          />
        </ChakraFlex>
      </ChakraFlex>
    </ModalV2>
  );
};

export default DismissAlertModal;
